import React, { useCallback, useEffect, useState } from 'react';
import DeploymentSafetyPanel from '../../components/admin/DeploymentSafetyPanel';

import AdminStatCard from '../../components/admin/AdminStatCard';
import { formatAdminDuration } from '../../components/admin/adminFormatting';
import { Button, Card, PageHeader } from '@copilot/ui';
import { EmptyState } from '@copilot/ui';
import Loader from '../../components/common/Loader';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as adminApi from '../../services/adminApi';

const RANGE_OPTIONS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

function formatShortDate(isoDate) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function ActivityChart({ activity }) {
  if (!activity || activity.length === 0) {
    return (
      <EmptyState
        title="No meeting activity yet."
        description="Activity will appear here once meetings are created."
      />
    );
  }

  const max = Math.max(...activity.map((point) => point.count), 1);

  return (
    <div className="admin-activity-chart">
      {activity.map((point) => (
        <div className="admin-activity-bar-col" key={point.date}>
          <span className="admin-activity-bar-value">{point.count}</span>
          <div className="admin-activity-bar-track">
            <div
              className="admin-activity-bar"
              style={{ height: `${(point.count / max) * 100}%` }}
              title={`${point.date}: ${point.count} meeting${point.count === 1 ? '' : 's'}`}
            />
          </div>
          <span className="admin-activity-bar-label">
            {formatShortDate(point.date)}
          </span>
        </div>
      ))}
    </div>
  );
}

// One shared bar row for both minute breakdowns (top users, plan
// distribution) - they differ only in what the label and the value
// mean, never in how they render.
function MinutesBreakdown({ rows, emptyTitle, emptyDescription, valueSuffix }) {
  if (!rows || rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  const max = Math.max(...rows.map((row) => row.value), 1);

  return (
    <div className="admin-minutes-list">
      {rows.map((row) => (
        <div className="admin-minutes-row" key={row.key}>
          <span className="admin-minutes-name">
            {row.label}
            {row.sublabel && <small>{row.sublabel}</small>}
          </span>
          <div className="admin-minutes-bar-track">
            <div
              className="admin-minutes-bar-fill"
              style={{ width: `${(row.value / max) * 100}%` }}
            />
          </div>
          <span className="admin-minutes-value">
            {row.value.toLocaleString()}
            {valueSuffix}
          </span>
        </div>
      ))}
    </div>
  );
}

const PROVIDER_CATEGORIES = [
  { key: 'stt', label: 'Speech-to-Text' },
  { key: 'tts', label: 'Text-to-Speech' },
  { key: 'translation', label: 'Translation' },
];

function ProviderUsage({ providerUsage }) {
  const groups = PROVIDER_CATEGORIES.map(({ key, label }) => ({
    key,
    label,
    entries: Object.entries(providerUsage?.[key] || {}).sort((a, b) => b[1] - a[1]),
  })).filter((group) => group.entries.length > 0);

  if (groups.length === 0) {
    return (
      <EmptyState
        title="No provider usage yet."
        description="Provider usage will appear here once meetings are created."
      />
    );
  }

  return (
    <div className="admin-provider-usage">
      {groups.map((group) => {
        const max = Math.max(...group.entries.map(([, count]) => count));

        return (
          <div className="admin-provider-usage-group" key={group.key}>
            <div className="admin-provider-usage-group-label">{group.label}</div>
            {group.entries.map(([provider, count]) => (
              <div className="admin-provider-usage-row" key={provider}>
                <span className="admin-provider-usage-name">
                  {capitalize(provider)}
                </span>
                <div className="admin-provider-usage-bar-track">
                  <div
                    className="admin-provider-usage-bar-fill"
                    style={{ width: `${(count / max) * 100}%` }}
                  />
                </div>
                <span className="admin-provider-usage-count">{count}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Bumped by the Retry button to re-run the effect below even when
  // `range` hasn't changed - avoids a second, duplicate fetch function.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const result = await adminApi.getAdminDashboard({ range });
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load dashboard data.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [range, reloadToken]);

  // Silent re-read: no loading/error state, last good data kept on failure.
  const refreshDashboard = useCallback(
    async ({ isStale }) => {
      const result = await adminApi.getAdminDashboard({ range });
      if (!isStale()) setData(result);
    },
    [range]
  );

  useAutoRefresh(refreshDashboard);

  const averageDurationSeconds =
    data && data.meetings.total > 0
      ? Math.round(data.meetings.duration_seconds / data.meetings.total)
      : 0;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Monitor KrishvaQ platform activity and system usage."
      />
      <DeploymentSafetyPanel />

      {isLoading && <Loader label="Loading dashboard..." />}

      {!isLoading && error && (
        <EmptyState
          title="Unable to load dashboard"
          description={error}
          action={
            <Button onClick={() => setReloadToken((token) => token + 1)}>
              Retry
            </Button>
          }
        />
      )}

      {!isLoading && !error && data && (
        <>
          <div className="stat-cards admin-stat-cards">
            <AdminStatCard
              title="Total Users"
              value={data.users.total}
              icon="user"
            />
            <AdminStatCard
              title="Active Users"
              value={data.users.active}
              icon="user"
            />
            <AdminStatCard
              title="Total Meetings"
              value={data.meetings.total}
              icon="meeting"
            />
            <AdminStatCard
              title="Total Questions"
              value={data.usage.questions}
              icon="question"
            />
            <AdminStatCard
              title="AI Responses"
              value={data.usage.ai_responses}
              icon="activity"
            />
            <AdminStatCard
              title="Avg. Meeting Duration"
              value={formatAdminDuration(averageDurationSeconds, '0 min')}
              icon="clock"
            />
            <AdminStatCard
              title="AI Minutes Consumed"
              value={data.minutes.total.toLocaleString()}
              icon="zap"
              description="All time"
            />
            <AdminStatCard
              title="AI Minutes This Month"
              value={data.minutes.this_month.toLocaleString()}
              icon="calendar"
            />
          </div>

          <div className="dashboard-grid admin-dashboard-grid">
            <Card interactive className="dashboard-main-card">
              <div className="admin-card-header-row">
                <div className="card-title">Meeting Activity</div>
                <div className="admin-range-toggle">
                  {RANGE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`admin-range-toggle-btn ${
                        range === option.value ? 'active' : ''
                      }`}
                      onClick={() => setRange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <ActivityChart activity={data.activity} />
            </Card>

            <Card interactive title="Provider Usage" className="dashboard-side-card">
              <ProviderUsage providerUsage={data.provider_usage} />
            </Card>
          </div>

          <div className="dashboard-grid admin-dashboard-grid">
            <Card
              interactive
              title="Most Active Users (AI Minutes)"
              className="dashboard-main-card"
            >
              <MinutesBreakdown
                rows={data.minutes.top_users.map((row) => ({
                  key: row.user_id,
                  label: row.full_name,
                  sublabel: row.email,
                  value: row.minutes,
                }))}
                emptyTitle="No AI minutes consumed yet."
                emptyDescription="Usage will appear here once meetings are completed."
                valueSuffix=" min"
              />
            </Card>

            <Card interactive title="Plan Distribution" className="dashboard-side-card">
              <MinutesBreakdown
                rows={data.minutes.plan_distribution.map((row) => ({
                  key: row.plan,
                  label: row.plan,
                  value: row.subscribers,
                }))}
                emptyTitle="No pricing plans configured."
                emptyDescription="Create plans in Pricing to see subscribers by plan."
                valueSuffix=" users"
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminDashboardPage;
